import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTable, useSortBy, Column } from "react-table";
import { Table } from "reactstrap";
import { Dropdown } from "react-bootstrap";
import * as actions from "../../../Redux/Actions";
import useLoadingIndicator from "../../../component/Loader/Loader";
import ModalComponents from "../../../component/ModalComponent/ModalComponent";
import AddTask from "../TaskDetails/AddTask";
import EditTask from "../TaskDetails/EditTask";
import TaskCard from "../TaskCard/TaskCard";
import { showErrorToastMessage } from "../../../component/Toasts/Toasts";
import ShowEntries from "../../../component/ShowEntries/ShowEntries";
import { TiArrowSortedDown, TiArrowSortedUp, TiArrowUnsorted } from "react-icons/ti";
import "./HandleAllTracker.scss";
import "./InseedMaster.scss";
import ViewAuditLogModal from '../TaskDetails/ViewAuditLogModal';
import ModalWarning from "../../../component/MakerChecker/ModalWarning";
import { FiMoreVertical } from 'react-icons/fi';
import { IAdvancedFilters } from '../AdvancedFilterModal';

interface IApiFilters {
  page?: number;
  limit?: number;
  search?: string;
  activityType?: string | null;
  myTasksOnly?: string;
  [key: string]: any;
}

interface TaskData {
  _id: string;
  taskName: string;
  employeeName?: string;
  employeeId: any;
  employeeCode?: string;
  clientName?: string;
  clientId: string;
  activityType: string;
  status: string;
  startDate: string;
  dueDate: string;
  createdAt: string;
  priority: string;
  comments: string;
}

const HandleAllTracker = (props: {
  activityTypeFilter: string | null;
  searchFilter: string;
  advancedFilters: IAdvancedFilters;
  myTasksOnly: boolean;
  refreshTrigger: number;
}) => {
  const { activityTypeFilter, searchFilter, advancedFilters, myTasksOnly, refreshTrigger } = props;
  const dispatch = useDispatch();
  const { loadingComponent, showLoadingIndicator, hideLoadingIndicator } = useLoadingIndicator();

  const { getAllHrbpActivities } = useSelector((state: any) => state.employeeReducer);

  const [paginationFilters, setPaginationFilters] = useState({ page: 1, limit: 10 });
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [showAuditLogModal, setShowAuditLogModal] = useState(false);
  const refreshTableData = () => {
    const finalFilters: IApiFilters = {
      ...paginationFilters,
      ...advancedFilters,
      activityType: activityTypeFilter,
      search: debouncedSearchValue,
      myTasksOnly: myTasksOnly ? 'true' : undefined,
    };
    dispatch(actions.getHrbpActivitiesAction(finalFilters));
    dispatch(actions.getHrbpSummaryAction());
    dispatch(actions.getHrbpDashboardDataAction());
  };

  const handleEditClick = (taskId: string) => { setSelectedTaskId(taskId); setShowEditModal(true); };
  const hideEditModal = () => { setSelectedTaskId(null); setShowEditModal(false); };
  const handleDeleteClick = (taskId: string) => { setSelectedTaskId(taskId); setShowDeleteModal(true); };
  const hideDeleteModal = () => { setSelectedTaskId(null); setShowDeleteModal(false); };
  const handleViewAuditLog = (taskId: string) => { setSelectedTaskId(taskId); setShowAuditLogModal(true); };
  const hideAuditLogModal = () => { setSelectedTaskId(null); setShowAuditLogModal(false); };
  const handlePageChange = (newPage: number) => { if (newPage > 0 && newPage <= paginationData.totalPages) setPaginationFilters(prev => ({ ...prev, page: newPage })); };
  const handlePageSizeChange = (newSize: number) => setPaginationFilters(prev => ({ ...prev, limit: newSize, page: 1 }));

  useEffect(() => {
    const timerId = setTimeout(() => setDebouncedSearchValue(searchFilter), 500);
    return () => clearTimeout(timerId);
  }, [searchFilter]);

  useEffect(() => {
    setPaginationFilters(prev => ({ ...prev, page: 1 }));
  }, [activityTypeFilter, debouncedSearchValue, advancedFilters, myTasksOnly]);

  useEffect(() => {
    showLoadingIndicator();

    const finalFilters: IApiFilters = {
      ...paginationFilters,
      ...advancedFilters,
      activityType: activityTypeFilter,
      search: debouncedSearchValue,
      myTasksOnly: myTasksOnly ? 'true' : undefined,
    };

    dispatch(
      actions.getHrbpActivitiesAction(finalFilters, (res: any) => {
        if (res?.error) showErrorToastMessage(res.error.error_description || "Failed to load activities");
        hideLoadingIndicator();
      })
    );
  }, [dispatch, paginationFilters, activityTypeFilter, debouncedSearchValue, advancedFilters, myTasksOnly, refreshTrigger]);


  const tableData = useMemo(() => getAllHrbpActivities?.data?.data || [], [getAllHrbpActivities]);
  const paginationData = useMemo(() => ({
    currentPage: getAllHrbpActivities?.data?.currentPage || 1,
    totalPages: getAllHrbpActivities?.data?.totalPages || 1,
    totalRecords: getAllHrbpActivities?.data?.totalRecords || 0,
  }), [getAllHrbpActivities]);

  const CustomToggle = React.forwardRef(({ children, onClick }: any, ref: any) => (
    <a href="" ref={ref} onClick={(e) => { e.preventDefault(); onClick(e); }} className="custom-dropdown-toggle">
      <FiMoreVertical size={20} />
    </a>
  ));

  const columns: readonly Column<TaskData>[] = useMemo(() => [
    {
      Header: 'S.No.',
      id: 'serialNumber',
      disableSortBy: true,
      Cell: ({ row }: { row: { index: number } }) => {
        const serialNumber = (paginationFilters.page - 1) * paginationFilters.limit + row.index + 1;
        return <span>{`TASK-${serialNumber}`}</span>;
      }
    },
    { Header: 'Task Name', accessor: 'taskName' },
    { Header: 'Employee', accessor: 'employeeName' },
    { Header: 'E-code', accessor: 'employeeCode' },
    { Header: 'Client', accessor: 'clientName' },
    { Header: 'Activity Type', accessor: 'activityType' },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }: { row: { original: TaskData } }) => {
        const { status, dueDate } = row.original;
        const isOverdue = new Date(dueDate) < new Date() && status.toLowerCase() !== 'closed';
        if (isOverdue) return <span className="status-pill status-overdue">Overdue</span>;
        const statusClass = `status-pill status-${status.toLowerCase()}`;
        return <span className={statusClass}>{status}</span>;
      }
    },
    {
      Header: 'Start Date',
      accessor: 'startDate',
      Cell: ({ value }: { value: string }) => <>{value ? new Date(value).toLocaleDateString() : 'N/A'}</>
    },
    {
      Header: 'Due Date',
      accessor: 'dueDate',
      Cell: ({ value }: { value: string }) => <>{value ? new Date(value).toLocaleDateString() : 'N/A'}</>
    },
    {
      Header: 'Age',
      accessor: 'createdAt',
      Cell: ({ value }: { value: string }) => {
        if (!value) return <>N/A</>;
        const ageInDays = Math.floor((new Date().getTime() - new Date(value).getTime()) / (1000 * 3600 * 24));
        return <>{`${ageInDays} days`}</>;
      }
    },
    {
      Header: 'Priority',
      accessor: 'priority',
      Cell: ({ value }: { value: string }) => {
        if (!value) return null;
        const priorityClass = `priority-pill priority-${value.toLowerCase()}`;
        return <span className={priorityClass}>{value}</span>;
      }
    },
    { Header: 'Comments', accessor: 'comments' },
    {
      Header: 'Actions', id: 'actions', disableSortBy: true,
      Cell: ({ row }: any) => (
        <Dropdown>
          <Dropdown.Toggle as={CustomToggle} />
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => handleEditClick(row.original._id)}>View & Edit Task</Dropdown.Item>
            <Dropdown.Item onClick={() => handleViewAuditLog(row.original._id)}>View Audit Log</Dropdown.Item>
            <Dropdown.Item className="text-danger" onClick={() => handleDeleteClick(row.original._id)}>Delete Task</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      ),
    },
  ], [paginationFilters.page, paginationFilters.limit]);

  const {
    getTableProps, getTableBodyProps, headerGroups, rows, prepareRow,
  } = useTable({ columns, data: tableData, manualPagination: true, manualSortBy: true }, useSortBy);


  return (
    <>
      {loadingComponent()}

      <div className="educational-container tracker-layout">
        <div className="card-div-container">
          <Table className="table-conatiner" hover {...getTableProps()}>
            <thead className="table-head">
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      title={undefined}
                      style={{ minWidth: '90px' }}
                    >
                      {column.render('Header')}
                      <span className="sort-icon">
                        {column.isSorted ? (column.isSortedDesc ? <TiArrowSortedDown /> : <TiArrowSortedUp />) : (column.canSort ? <TiArrowUnsorted /> : '')}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="table-body" {...getTableBodyProps()}>
              {rows.length > 0 ? (
                rows.map(row => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map(cell => (
                        <td
                          className="table-td"
                          {...cell.getCellProps()}
                          style={cell.column.id === 'serialNumber' ? { minWidth: '70px' } : {}}
                        >
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center">No tasks found.</td>
                </tr>
              )}
            </tbody>
          </Table>
          <div className="d-flex justify-content-between align-items-center mt-3 p-3">
            <ShowEntries pageSize={paginationFilters.limit} setPageSize={handlePageSizeChange} totalEntries={paginationData.totalRecords} />
            <nav>
              <ul className="pagination">
                <li className={`page-item ${paginationData.currentPage === 1 ? 'disabled' : ''}`}>
                  <a className="page-link" href="#" onClick={() => handlePageChange(paginationData.currentPage - 1)}>‹</a>
                </li>
                {Array.from({ length: paginationData.totalPages }, (_, i) => i + 1).map(number => (
                  <li key={number} className={`page-item ${paginationData.currentPage === number ? 'active' : ''}`}>
                    <a className="page-link" href="#" onClick={() => handlePageChange(number)}>{number}</a>
                  </li>
                ))}
                <li className={`page-item ${paginationData.currentPage === paginationData.totalPages ? 'disabled' : ''}`}>
                  <a className="page-link" href="#" onClick={() => handlePageChange(paginationData.currentPage + 1)}>›</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
        <TaskCard />
      </div>

      <ModalComponents show={showEditModal} onHide={hideEditModal} title="Edit Task Details" modalBody={<EditTask taskId={selectedTaskId} hideEditModal={hideEditModal} onUpdateSuccess={refreshTableData} />} className="modal-lg" />
      <ModalComponents show={showAuditLogModal} onHide={hideAuditLogModal} title="Task History (Audit Log)" modalBody={<ViewAuditLogModal taskId={selectedTaskId} />} className="modal-xl" />
      <ModalComponents show={showDeleteModal} onHide={hideDeleteModal} title="Confirm Deletion" modalBody={<ModalWarning hideModal={hideDeleteModal} header={"Delete this task"} id={selectedTaskId} editApi={actions.deleteHrbpActivityAction} data={{}} hideEditModal={hideDeleteModal} apiCallCompleted={() => { }} getApiCall={refreshTableData} setShowUndoPopup={() => { }} />} className="modal-md" />
    </>
  );
};

export default HandleAllTracker;